import { supabase } from '../lib/supabase';

/**
 * Synchronise les lignes budgétaires vers le plan de trésorerie
 * Répartit les montants budgétaires annuels sur 12 mois
 *
 * @param userId - ID de l'utilisateur
 * @param year - Année budgétaire à synchroniser
 * @param fiscalYearStartMonth - Mois de début d'exercice (1-12)
 * @param scenarioId - ID du scénario de trésorerie (optionnel, null = plan principal)
 * @param syncIncome - Synchroniser les recettes (défaut: true)
 * @param syncExpense - Synchroniser les dépenses (défaut: true)
 * @returns Nombre de lignes créées/mises à jour
 */
export async function syncBudgetToTreasury(
  userId: string,
  year: number,
  fiscalYearStartMonth: number = 9,
  scenarioId: string | null = null,
  syncIncome: boolean = true,
  syncExpense: boolean = true
): Promise<{ success: boolean; linesUpdated: number; error?: string }> {
  try {
    // 1. Charger toutes les lignes budgétaires pour l'année
    const { data: budgetLines, error: budgetError } = await supabase
      .from('budget_lines')
      .select(`
        id,
        category_id,
        year,
        planned_amount,
        categories (
          type,
          name
        )
      `)
      .eq('user_id', userId)
      .eq('year', year);

    if (budgetError) {
      return { success: false, linesUpdated: 0, error: budgetError.message };
    }

    if (!budgetLines || budgetLines.length === 0) {
      return { success: true, linesUpdated: 0, error: 'Aucune ligne budgétaire à synchroniser' };
    }

    let linesUpdated = 0;

    // 2. Pour chaque ligne budgétaire, créer/mettre à jour les 12 lignes de trésorerie
    for (const budgetLine of budgetLines) {
      const category = budgetLine.categories as any;

      // Filtrer selon les options choisies
      if (category?.type === 'income' && !syncIncome) {
        continue;
      }
      if (category?.type === 'expense' && !syncExpense) {
        continue;
      }

      const monthlyAmount = Number(budgetLine.planned_amount) / 12;

      // Créer les 12 mois de l'exercice
      for (let monthOffset = 0; monthOffset < 12; monthOffset++) {
        const month = ((fiscalYearStartMonth - 1 + monthOffset) % 12) + 1;
        const yearOffset = Math.floor((fiscalYearStartMonth - 1 + monthOffset) / 12);
        const forecastYear = year + yearOffset;

        // Vérifier si une ligne existe déjà
        let query = supabase
          .from('cash_flow_forecast_lines')
          .select('id, amount')
          .eq('user_id', userId)
          .eq('category_id', budgetLine.category_id)
          .eq('forecast_month', month)
          .eq('forecast_year', forecastYear);

        if (scenarioId === null) {
          query = query.is('scenario_id', null);
        } else {
          query = query.eq('scenario_id', scenarioId);
        }

        const { data: existingLines } = await query;

        const lineData = {
          user_id: userId,
          forecast_year: forecastYear,
          forecast_month: month,
          category_id: budgetLine.category_id,
          description: `Synchronisé depuis budget ${category?.name || ''}`,
          amount: category?.type === 'income' ? monthlyAmount : -monthlyAmount,
          type: category?.type || 'expense',
          scenario_id: scenarioId,
          updated_at: new Date().toISOString()
        };

        if (existingLines && existingLines.length > 0) {
          // Mettre à jour la ligne existante
          const { error: updateError } = await supabase
            .from('cash_flow_forecast_lines')
            .update({
              amount: lineData.amount,
              description: lineData.description,
              updated_at: lineData.updated_at
            })
            .eq('id', existingLines[0].id);

          if (!updateError) {
            linesUpdated++;
          }
        } else {
          // Créer une nouvelle ligne
          const { error: insertError } = await supabase
            .from('cash_flow_forecast_lines')
            .insert([lineData]);

          if (!insertError) {
            linesUpdated++;
          }
        }
      }
    }

    return {
      success: true,
      linesUpdated,
      error: undefined
    };
  } catch (error: any) {
    return {
      success: false,
      linesUpdated: 0,
      error: error.message || 'Erreur inconnue lors de la synchronisation'
    };
  }
}

/**
 * Supprime toutes les lignes de trésorerie synchronisées depuis le budget pour une année
 *
 * @param userId - ID de l'utilisateur
 * @param year - Année à nettoyer
 * @param scenarioId - ID du scénario (optionnel)
 */
export async function clearSyncedTreasuryLines(
  userId: string,
  year: number,
  scenarioId: string | null = null
): Promise<{ success: boolean; error?: string }> {
  try {
    let query = supabase
      .from('cash_flow_forecast_lines')
      .delete()
      .eq('user_id', userId)
      .eq('forecast_year', year)
      .like('description', 'Synchronisé depuis budget%');

    if (scenarioId === null) {
      query = query.is('scenario_id', null);
    } else {
      query = query.eq('scenario_id', scenarioId);
    }

    const { error } = await query;

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Erreur lors de la suppression'
    };
  }
}

/**
 * Calcule l'écart entre le budget et le réalisé pour une catégorie et un mois
 *
 * @param userId - ID de l'utilisateur
 * @param categoryId - ID de la catégorie
 * @param month - Mois (1-12)
 * @param year - Année
 */
export async function calculateBudgetVariance(
  userId: string,
  categoryId: string,
  month: number,
  year: number
): Promise<{
  budgeted: number;
  actual: number;
  variance: number;
  variancePercent: number;
}> {
  // Récupérer le montant budgété pour cette catégorie (budget annuel / 12)
  const { data: budgetLine } = await supabase
    .from('budget_lines')
    .select('planned_amount')
    .eq('user_id', userId)
    .eq('category_id', categoryId)
    .eq('year', year)
    .maybeSingle();

  const monthlyBudget = budgetLine ? Number(budgetLine.planned_amount) / 12 : 0;

  // Récupérer les transactions réelles pour ce mois
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, is_internal_transfer')
    .eq('user_id', userId)
    .eq('category_id', categoryId)
    .gte('date', monthStart)
    .lte('date', monthEnd);

  const actualAmount = transactions
    ? transactions
        .filter(t => !t.is_internal_transfer)
        .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0)
    : 0;

  const variance = actualAmount - monthlyBudget;
  const variancePercent = monthlyBudget > 0 ? (variance / monthlyBudget) * 100 : 0;

  return {
    budgeted: monthlyBudget,
    actual: actualAmount,
    variance,
    variancePercent
  };
}
